import React, { useState, useEffect } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem,
    Snackbar,
    Alert,
    CircularProgress,
    useTheme,
    alpha
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchInvestments } from '../../store/investSlice';
import { ApiHelper } from '../../utils/apiHelper';

export default function InvestmentForm({ campaignId }) {
    const dispatch = useDispatch();
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [formData, setFormData] = useState({
        paymentType: '',
        paymentDetail: '',
        amount: '',
        status: 'pending'
    });
    const [snackbarMessage, setSnackbarMessage] = useState({
        severity: 'success',
        message: 'Yatırım başarıyla eklendi!'
    });
    const theme = useTheme();

    const handleOpenDialog = () => setOpenDialog(true);
    const handleCloseDialog = () => setOpenDialog(false);

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const fetchUserInvestments = async () => {
        try {
            setLoading(true);
            const response = await ApiHelper.get('/investment/my-investment');
            if (response.data.code === "00000") {
                setPayments(response.data.investment);
            }
        } catch (error) {
            console.error("Error fetching user investments:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (campaignId) {
            dispatch(fetchInvestments(campaignId))
                .unwrap()
                .then(data => {
                    setPayments(data);
                    setLoading(false);
                })
                .catch(error => {
                    console.error("Error fetching campaign investments:", error);
                    setLoading(false);
                });
        } else {
            fetchUserInvestments();
        }
    }, [dispatch, campaignId]);

    const handleSubmit = async () => {
        try {
            const response = await ApiHelper.post('/investment', {
                startupId: campaignId,
                paymentType: formData.paymentType,
                amount: formData.amount
            });

            if (response.data.code === "00000") {
                // if (paymentType === "CREDIT_CARD") {
                //     window.location.href = response.data.paymentUrl;
                // }
                setOpenDialog(false);
                setOpenSnackbar(true);
                if (campaignId) {
                    dispatch(fetchInvestments(campaignId));
                } else {
                    fetchUserInvestments();
                }
                
                setFormData({
                    paymentType: '',
                    paymentDetail: '',
                    amount: '',
                    status: 'pending'
                });
            }
        } catch (error) {
            console.error("Error making investment:", error);
            setOpenSnackbar(true);
            setSnackbarMessage({
                severity: 'error',
                message: 'Yatırım yapılırken bir hata oluştu!'
            });
        }
    };

    const getPaymentTypeLabel = (type) => {
        switch (type) {
            case 'CREDIT_CARD':
                return 'Kredi Kartı';
            case 'BANK_TRANSFER':
                return 'EFT/Havale';
            default:
                return type;
        }
    };

    const getStatusStyle = (status) => {
        switch (status?.toLowerCase()) {
            case 'pending':
                return {
                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                    color: theme.palette.warning.dark,
                    borderRadius: 1,
                    py: 0.5,
                    px: 1,
                    display: 'inline-block'
                };
            case 'approved':
                return {
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    color: theme.palette.success.dark,
                    borderRadius: 1,
                    py: 0.5,
                    px: 1,
                    display: 'inline-block'
                };
            case 'rejected':
                return {
                    bgcolor: alpha(theme.palette.error.main, 0.1),
                    color: theme.palette.error.dark,
                    borderRadius: 1,
                    py: 0.5,
                    px: 1,
                    display: 'inline-block'
                };
            case 'waiting_sent':
                return {
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    color: theme.palette.info.dark,
                    borderRadius: 1,
                    py: 0.5,
                    px: 1,
                    display: 'inline-block'
                };
            default:
                return {};
        }
    };

    const getStatusLabel = (status) => {
        switch (status?.toLowerCase()) {
            case 'pending':
                return 'Onay Bekliyor';
            case 'approved':
                return 'Onaylandı';
            case 'rejected':
                return 'Reddedildi';
            case 'waiting_sent':
                return 'Gönderim Bekleniyor';
            default:
                return status;
        }
    };

    if (loading) {
        return <CircularProgress />;
    }

    return (
        <>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Ödeme Tipi</TableCell>
                            <TableCell>Detay</TableCell>
                            <TableCell>Miktar</TableCell>
                            <TableCell>Durum</TableCell>
                            <TableCell>Tarih</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {payments && payments.length > 0 ? (
                            payments.map((payment) => (
                                <TableRow key={payment.investmentId || payment.id}>
                                    <TableCell>{getPaymentTypeLabel(payment.paymentType)}</TableCell>
                                    <TableCell>{payment.paymentDetail}</TableCell>
                                    <TableCell>{payment.amount} TL</TableCell>
                                    <TableCell>
                                        <Box sx={getStatusStyle(payment.status)}>
                                            {getStatusLabel(payment.status)}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {new Date(parseInt(payment.registerTime) || payment.registerTime).toLocaleDateString('tr-TR')}
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    Henüz yatırım bulunmamaktadır
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogTitle>Yeni Yatırım Ekle</DialogTitle>
                <DialogContent>
                    <TextField
                        select
                        fullWidth
                        margin="normal"
                        name="paymentType"
                        label="Ödeme Tipi"
                        value={formData.paymentType}
                        onChange={handleInputChange}
                    >
                        <MenuItem value="CREDIT_CARD">Kredi Kartı</MenuItem>
                        <MenuItem value="BANK_TRANSFER">Havale/EFT</MenuItem>
                    </TextField>
                    <TextField
                        fullWidth
                        margin="normal"
                        name="paymentDetail"
                        label="Ödeme Detayı"
                        value={formData.paymentDetail}
                        onChange={handleInputChange}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        name="amount"
                        label="Miktar"
                        type="number"
                        value={formData.amount}
                        onChange={handleInputChange}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>İptal</Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary">
                        Kaydet
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={openSnackbar}
                autoHideDuration={3000}
                onClose={() => setOpenSnackbar(false)}
            >
                <Alert severity={snackbarMessage.severity} sx={{ width: '100%' }}>
                    {snackbarMessage.message}
                </Alert>
            </Snackbar>
        </>
    );
}
